import { Paper, Box, Typography, Avatar, IconButton, Divider, Slider, TextField, Collapse, Tooltip } from "@mui/material";
import React from "react";
import { SelectedAssetItemProps } from "../../../../types/cashout.types";
import {
    Close as CloseIcon,
    ExpandMore as ExpandMoreIcon,
    ErrorOutline as ErrorOutlineIcon,
} from '@mui/icons-material';
import { usePriceFormatter } from "../../../../hooks";
import { useAppSelector } from "../../../../Redux/hooks";

function SelectedAssetItem({
    asset,
    isExpanded,
    hasError = false,
    errorMessage,
    onToggleExpand,
    onSliderChange,
    onAmountChange,
    onRemove,
}: SelectedAssetItemProps) {
    // Calculate display values from Redux state
    const selectedCashOutAmount = asset.selectedCashOutQuantity * asset.rate;
    const currencyInfo = useAppSelector((state) => state.currency);
    const minAmount = asset.minimumCashOutQuantity * asset.rate;
    const maxAmount = asset.eligibleCashOutQuantity * asset.rate;
    const minPercentage = (asset.minimumCashOutQuantity / asset.eligibleCashOutQuantity) * 100;
    const formatter = usePriceFormatter();

    const [tooltipOpen, setTooltipOpen] = React.useState(false);
    const [inputValue, setInputValue] = React.useState(formatter.amount(selectedCashOutAmount).display);
    const [isFocused, setIsFocused] = React.useState(false);

    // Sync input with Redux state when not focused
    React.useEffect(() => {
        if (!isFocused) {
            setInputValue(formatter.amount(selectedCashOutAmount, { showSymbol: false }).display);
        }
    }, [selectedCashOutAmount, isFocused]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            setInputValue(value);
        }
    };

    const handleInputFocus = () => {
        setIsFocused(true);
    };

    const handleInputBlur = () => {
        setIsFocused(false);
        const numValue = parseFloat(inputValue);

        if (isNaN(numValue) || numValue <= 0) {
            // Reset to current state
            setInputValue(formatter.amount(selectedCashOutAmount, { showSymbol: false }).display);
        } else {
            // Clamp and dispatch
            const clampedAmount = Math.min(Math.max(numValue, minAmount), maxAmount);
            setInputValue(formatter.amount(clampedAmount, { showSymbol: false }).display);
            onAmountChange({ target: { value: clampedAmount.toString() } } as any);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            (e.target as HTMLInputElement).blur();
        }
    };

    return (
        <Paper
            sx={{
                backgroundColor: hasError ? 'rgba(255, 107, 107, 0.06)' : 'rgba(255, 255, 255, 0.05)',
                border: hasError ? '1px solid rgba(255, 107, 107, 0.35)' : '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: { xs: '8px', sm: '10px' },
                padding: { xs: '12px', sm: '16px' },
                display: 'flex',
                flexDirection: 'column',
                gap: '0px',
                overflow: 'hidden',
                transition: 'all 0.2s ease',
                '&:hover': {
                    backgroundColor: hasError ? 'rgba(255, 107, 107, 0.1)' : 'rgba(255, 255, 255, 0.08)',
                    borderColor: hasError ? 'rgba(255, 107, 107, 0.5)' : 'rgba(255, 255, 255, 0.15)',
                },
            }}
        >
            {/* Error Banner */}
            {hasError && errorMessage && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        marginBottom: '10px',
                        padding: '5px 8px',
                        backgroundColor: 'rgba(255,107,107,0.1)',
                        borderRadius: '6px',
                    }}
                >
                    <ErrorOutlineIcon sx={{ fontSize: '13px', color: '#FF6B6B', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '11px', color: 'rgba(255,107,107,0.9)', fontFamily: 'Avenir', lineHeight: 1.4 }}>
                        {errorMessage.includes(': ') ? errorMessage.slice(errorMessage.indexOf(': ') + 2) : errorMessage}
                    </Typography>
                </Box>
            )}

            {/* Asset Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                    gap: { xs: 1, sm: 0 },
                }}
            >
                {/* Logo + Name */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '8px', sm: '10px' }, flex: 1, minWidth: 0 }}>
                    <Avatar
                        src={asset.logo ?? undefined}
                        sx={{
                            width: { xs: '32px', sm: '36px' },
                            height: { xs: '32px', sm: '36px' },
                            backgroundColor: 'rgba(74, 122, 242, 0.2)',
                        }}
                    >
                        {asset.symbol?.substring(0, 2)?.toUpperCase() || 'CO'}
                    </Avatar>

                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                            sx={{
                                fontSize: { xs: '13px', sm: '14px' },
                                fontWeight: '800',
                                color: '#FFFFFF',
                                fontFamily: 'Avenir',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {asset.name}
                        </Typography>
                        <Typography sx={{ fontSize: { xs: '11px', sm: '12px' }, color: 'rgba(255, 255, 255, 0.5)', fontFamily: 'Avenir' }}>
                            {asset.symbol}
                        </Typography>
                    </Box>
                </Box>

                {/* Amount + Controls */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '6px', sm: '8px' } }}>
                    <Typography
                        sx={{
                            fontSize: { xs: '13px', sm: '14px' },
                            fontWeight: '800',
                            color: hasError ? '#FF6B6B' : '#4A7AF2',
                            fontFamily: 'Avenir',
                            minWidth: { xs: '60px', sm: '70px' },
                            textAlign: 'right',
                        }}
                    >
                        {formatter.amount(selectedCashOutAmount).display}
                    </Typography>

                    <IconButton
                        onClick={onToggleExpand}
                        size="small"
                        sx={{
                            width: '24px',
                            height: '24px',
                            padding: 0,
                            color: 'rgba(255, 255, 255, 0.7)',
                            transition: 'all 0.3s ease',
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)', color: '#FFFFFF' },
                        }}
                    >
                        <ExpandMoreIcon sx={{ fontSize: { xs: '18px', sm: '20px' } }} />
                    </IconButton>

                    <IconButton
                        onClick={(e) => { e.stopPropagation(); onRemove(); }}
                        size="small"
                        sx={{
                            width: '24px',
                            height: '24px',
                            padding: 0,
                            transition: 'all 0.2s ease',
                            '&:hover': { backgroundColor: 'rgba(255, 107, 107, 0.2)' },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: { xs: '16px', sm: '18px' }, color: 'rgba(255, 255, 255, 0.6)' }} />
                    </IconButton>
                </Box>
            </Box>

            {/* Expandable Controls */}
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Divider sx={{ borderColor: '#FFFFFF70', marginY: { xs: 1.5, sm: 2 } }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: '12px', sm: '16px' } }}>
                    <Box sx={{ display: 'flex', gap: { xs: '15px', sm: '25px' }, alignItems: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>

                        {/* Slider */}
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', width: '100%' }}>
                            <Tooltip
                                title={`${asset.slider.toFixed(0)}% • ${formatter.display(selectedCashOutAmount)} (${formatter.quantity(asset.selectedCashOutQuantity).display} ${asset.symbol})`} open={tooltipOpen}
                                arrow
                            >
                                <Box onMouseEnter={() => setTooltipOpen(true)} onMouseLeave={() => setTooltipOpen(false)}>
                                    <Slider
                                        value={asset.slider}
                                        min={minPercentage}
                                        max={100}
                                        step={0.1}
                                        onChange={(_, value) => { onSliderChange(value); setTooltipOpen(true); }}
                                        onChangeCommitted={() => setTooltipOpen(false)}
                                        sx={{
                                            width: '100%',
                                            maxWidth: { sm: 250 },
                                            height: 6,
                                            padding: '10px 0',
                                            '& .MuiSlider-rail': { height: 6, borderRadius: 10, backgroundColor: 'rgba(255, 255, 255, 0.1)', opacity: 1 },
                                            '& .MuiSlider-track': { height: 6, borderRadius: 10, background: 'linear-gradient(90deg, #F2674A 0%, #E85A42 100%)', border: 'none' },
                                            '& .MuiSlider-thumb': {
                                                width: { xs: 18, sm: 20 },
                                                height: { xs: 18, sm: 20 },
                                                background: 'linear-gradient(135deg, #F2674A 0%, #4A7AF2 100%)',
                                                boxShadow: '0px 2px 8px rgba(242, 103, 74, 0.4)',
                                                border: '2px solid #FFFFFF',
                                                '&::before': { content: '""', position: 'absolute', width: 10, height: 10, borderRadius: '50%', backgroundColor: '#FFFFFF', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', boxShadow: 'none' },
                                            },
                                            '&::before': { content: '""', position: 'absolute', left: 0, top: '50%', width: { xs: 10, sm: 12 }, height: { xs: 10, sm: 12 }, borderRadius: '50%', backgroundColor: '#F2674A', transform: 'translate(-50%, -50%)', zIndex: 1 },
                                            '&::after': { content: '""', position: 'absolute', right: 0, top: '50%', width: { xs: 10, sm: 12 }, height: { xs: 10, sm: 12 }, borderRadius: '50%', backgroundColor: '#4A4A4A', transform: 'translate(50%, -50%)', zIndex: 1 },
                                        }}
                                    />
                                </Box>
                            </Tooltip>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', maxWidth: { sm: 250 } }}>
                                <Typography sx={{ fontSize: { xs: '9px', sm: '10px' }, fontFamily: 'Avenir', color: 'rgba(255,255,255,0.5)' }}>
                                    Min. {formatter.amount(minAmount).display}
                                </Typography>
                                <Typography sx={{ fontSize: { xs: '9px', sm: '10px' }, fontFamily: 'Avenir', color: 'rgba(255,255,255,0.5)' }}>
                                    Max. {formatter.amount(maxAmount).display}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Amount Input */}
                        <TextField
                            type="text"
                            value={inputValue}
                            onChange={handleInputChange}
                            onFocus={handleInputFocus}
                            onBlur={handleInputBlur}
                            onKeyPress={handleKeyPress}
                            variant="outlined"
                            InputProps={{
                                startAdornment: (
                                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: { xs: '13px', sm: '14px' }, marginRight: '4px' }}>
                                        {currencyInfo?.symbol}
                                    </Typography>
                                ),
                                sx: {
                                    height: { xs: '40px', sm: '44px' },
                                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                    fontSize: { xs: '13px', sm: '14px' },
                                    color: '#FFFFFF',
                                    fontFamily: 'Avenir',
                                },
                            }}
                            sx={{
                                width: { xs: '100%', sm: '100px' },
                                '& .MuiOutlinedInput-root': { borderRadius: '10px !important' },
                                '& .MuiOutlinedInput-input': { textAlign: 'end !important' },
                            }}
                        />
                    </Box>

                    {/* Optional: Show quantity info */}
                    {/* <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', fontFamily: 'Avenir' }}>
                            Quantity: {asset.selectedCashOutQuantity.toFixed(8)} {asset.symbol}
                        </Typography>
                        <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.4)', fontFamily: 'Avenir' }}>
                            @ ${asset.rate.toFixed(2)}
                        </Typography>
                    </Box> */}
                </Box>
            </Collapse>
        </Paper>
    );
}

export default SelectedAssetItem;